import React from "react";
import { toast } from "sonner";
import { Truck, Navigation, Route, Camera, PenLine, AlertTriangle, Check, Phone, Download, Send, X, Loader2, SwitchCamera } from "lucide-react";
import {
  STATUS_MAP, sLabel, mapsUrl, isPickedUp, ANOMALY_TYPES,
  getPrimaryStopDetails, getSecondaryStopDetails,
  getPrimaryStopSummary,
  downloadPdf, getReadyBadgeLabel,
  formatDateTimeIT,
} from "../helpers";
import { UrgencyBadge } from "../UrgencyBadge";
import { DriverHeader } from "../DriverHeader";

export function DetailScreen(ctx) {
  const {
    driverInfo, selectedJob, allItems, myLocation, saving,
    pushEnabled, installPrompt, setInstallPrompt,
    setShowScanner, togglePush, logout, navigateTo, goBack,
    updateStatus, capturePhoto, setPodPhoto, setShowAnomaly,
    showAnomaly, anomalyType, setAnomalyType, anomalyNote, setAnomalyNote,
    anomalyPhoto, setAnomalyPhoto, submitAnomaly,
    toggleCamera, preferredCamera,
  } = ctx;

  const job = allItems.find((i) => i.id === selectedJob.id) || selectedJob;
  const isShipment = job._type === "shipment";
  const pickedUp = isPickedUp(job);
  const primary = getPrimaryStopDetails(job);
  const secondary = getSecondaryStopDetails(job);
  const primaryUrl = mapsUrl(primary, myLocation ? [myLocation.lat, myLocation.lng] : null);
  const primaryLabel = pickedUp ? "Destinatario \u00b7 DA CONSEGNARE" : "Mittente \u00b7 DA RITIRARE";
  const secondaryLabel = pickedUp ? "Mittente (ritirato)" : "Destinatario (successivo)";

  const totalUnits = job.colli?.reduce((s, c) => s + (parseInt(c.quantita) || 1), 0) || 0;
  const totalWeight = job.colli?.reduce((s, c) => {
    try {
      const peso = parseFloat((c.peso || "0").replace(",", "."));
      return s + peso;
    } catch { return s; }
  }, 0) || 0;

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="driver-app">
      <DriverHeader showBack title={job.tracking_number} driverInfo={driverInfo} goBack={goBack} navigateTo={navigateTo} togglePush={togglePush} pushEnabled={pushEnabled} setShowScanner={setShowScanner} installPrompt={installPrompt} setInstallPrompt={setInstallPrompt} logout={logout} />

      <div className={`px-4 py-2 ${STATUS_MAP[job.status]?.bg || "bg-zinc-100"} flex items-center justify-between`}>
        <span className={`text-xs font-bold uppercase ${STATUS_MAP[job.status]?.text || ""}`}>{sLabel(job.status)}</span>
        <div className="flex items-center gap-2">
          <UrgencyBadge job={job} size="lg" context="detail" />
          <span className="text-[10px] text-zinc-500">{isShipment ? "Spedizione" : "Ritiro"}</span>
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        <div className={`rounded-lg p-3 flex items-center justify-between ${pickedUp ? "bg-green-600" : "bg-[#FF5200]"}`} data-testid="driver-next-action-pill">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/80">Prossima azione</p>
            <p className="text-base font-bold text-white">{pickedUp ? "VAI A CONSEGNARE" : "VAI A RITIRARE"}</p>
          </div>
          {pickedUp ? <Navigation className="text-white" size={28} /> : <Truck className="text-white" size={28} />}
        </div>

        {primary && (
          <div className={`bg-white rounded-lg border-2 p-4 space-y-2 ${pickedUp ? "border-green-500" : "border-[#FF5200]"}`} data-testid="driver-primary-stop">
            <p className={`text-[10px] font-bold uppercase tracking-widest ${pickedUp ? "text-green-700" : "text-[#FF5200]"}`}>{primaryLabel}</p>
            <p className="text-sm font-bold">{primary.company || primary.name}</p>
            {primary.company && primary.name && <p className="text-xs text-zinc-600">{primary.name}</p>}
            <p className="text-xs text-zinc-600">{primary.address}, {primary.cap} {primary.city} ({primary.province})</p>
            {primary.phone && (
              <a href={`tel:${primary.phone}`} className="inline-flex items-center gap-1.5 text-sm text-blue-600 font-bold mt-1" data-testid="call-recipient">
                <Phone size={14} /> {primary.phone}
              </a>
            )}
            {job.note && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                <p className="text-[10px] font-bold uppercase text-amber-600 mb-1">Note</p>
                <p className="text-xs text-amber-800">{job.note}</p>
              </div>
            )}
            <div className="flex gap-2 mt-2">
              {primaryUrl && <a href={primaryUrl} target="_blank" rel="noopener noreferrer" className="flex-1 h-10 bg-[#09090B] text-white text-xs font-bold uppercase rounded-sm flex items-center justify-center gap-1.5" data-testid="nav-gmaps"><Navigation size={14} /> Google Maps</a>}
            </div>
          </div>
        )}

        {secondary && (
          <div className="bg-white rounded-lg border border-zinc-200 p-4 space-y-2 opacity-75" data-testid="driver-secondary-stop">
            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{secondaryLabel}</p>
            <p className="text-sm font-bold">{secondary.company || secondary.name}</p>
            {secondary.company && secondary.name && <p className="text-[11px] text-zinc-500">{secondary.name}</p>}
            <p className="text-xs text-zinc-600">{secondary.address}, {secondary.cap} {secondary.city}</p>
            {secondary.phone && <a href={`tel:${secondary.phone}`} className="text-xs text-blue-600 font-bold flex items-center gap-1"><Phone size={12} /> {secondary.phone}</a>}
          </div>
        )}

        <div className="bg-white rounded-lg border border-zinc-200 p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Cosa trasporta</p>
          {job.colli?.length > 0 ? (
            <div className="space-y-1.5">
              {job.colli.map((c, i) => {
                const qty = parseInt(c.quantita) || 1;
                const pesoNum = parseFloat((c.peso || "0").replace(",", ".")) || 0;
                const tipoLabel = c.tipo === "pallet" ? "Pallet" : (qty > 1 ? "Colli" : "Collo");
                return (
                  <div key={i} className="flex flex-col gap-0.5 text-sm py-1.5 border-b border-zinc-100 last:border-0">
                    <div className="flex justify-between items-center">
                      <span className="font-bold">
                        <span className="text-[#FF5200]">×{qty}</span> {tipoLabel}{qty > 1 ? ` (gruppo ${i + 1})` : ` ${i + 1}`}
                      </span>
                      <span className="text-zinc-700 font-medium">
                        {pesoNum > 0 ? `${pesoNum} kg` : "-"}
                      </span>
                    </div>
                    {c.dimensioni && <span className="text-xs text-zinc-500">Dimensioni: {c.dimensioni}</span>}
                  </div>
                );
              })}
              <div className="flex justify-between text-xs font-bold text-[#09090B] pt-2 border-t border-zinc-200">
                <span>{totalUnits} {totalUnits === 1 ? "collo" : "colli"} totali</span>
                {totalWeight > 0 && <span>Peso totale: {totalWeight.toFixed(1)} kg</span>}
              </div>
            </div>
          ) : <p className="text-sm text-zinc-500">Nessun dettaglio colli</p>}
          {job.tracking_number && <p className="text-xs text-zinc-400 mt-2">Bolla n. {job.tracking_number}</p>}
        </div>

        {job.extra_services && (job.extra_services.sponda || job.extra_services.camion_completo || job.extra_services.furgone_completo) && (
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-blue-700 mb-2">Servizi extra richiesti</p>
            <ul className="space-y-1 text-sm text-blue-900 font-medium">
              {job.extra_services.sponda && <li>• Servizio sponda (scarico assistito)</li>}
              {job.extra_services.camion_completo && (
                <li>• Camion completo{job.extra_services.camion_metri ? ` — ${job.extra_services.camion_metri} m di pianale` : ""}</li>
              )}
              {job.extra_services.furgone_completo && (
                <li>• Furgone completo{job.extra_services.furgone_num_mezzi ? ` — ${job.extra_services.furgone_num_mezzi} mezzi` : ""}</li>
              )}
            </ul>
          </div>
        )}

        {job.anomaly && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-[10px] font-bold uppercase text-red-600 mb-1">Anomalia segnalata</p>
            <p className="text-sm font-bold text-red-800">{job.anomaly.type}</p>
            {job.anomaly.note && <p className="text-xs text-red-700 mt-1">{job.anomaly.note}</p>}
          </div>
        )}

        {job.pod && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-[10px] font-bold uppercase text-green-600 mb-1">Prova di consegna</p>
            <p className="text-sm font-bold text-green-800">Firmato da: {job.pod.signed_by}</p>
            <p className="text-xs text-green-700">{formatDateTimeIT(job.pod.timestamp)}</p>
            {job.pod.photo && <img src={job.pod.photo} alt="POD" className="mt-2 rounded max-h-32 border" />}
            {job.pod.signature && <img src={job.pod.signature} alt="Firma" className="mt-2 bg-white rounded max-h-20 border p-1" />}
          </div>
        )}

        <div className="bg-white rounded-lg border border-zinc-200 p-4 space-y-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">Merce pronta</p>
            <p className="text-sm font-bold text-[#09090B]">{getReadyBadgeLabel(job) || "Non pronta"}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">DDT PDF</p>
            {job.ddt_files?.length ? (
              <div className="space-y-2">
                {job.ddt_files.map((file) => (
                  <button key={file.id} onClick={() => downloadPdf(file)} className="w-full flex items-center justify-between text-left border border-zinc-200 rounded-sm p-3" data-testid={`driver-download-ddt-${file.id}`}>
                    <div>
                      <p className="text-sm font-bold text-[#09090B]">{file.filename}</p>
                      <p className="text-xs text-zinc-500">PDF consultabile dall&rsquo;autista</p>
                    </div>
                    <Download size={16} className="text-zinc-400" />
                  </button>
                ))}
              </div>
            ) : <p className="text-xs text-zinc-500">Nessun DDT disponibile.</p>}
          </div>
        </div>

        {/* Client Info (compact, in fondo) */}
        <div className="bg-zinc-50 rounded border border-zinc-200 px-3 py-2 flex items-center justify-between gap-2" data-testid="driver-client-info-compact">
          <div className="min-w-0">
            <p className="text-[9px] font-bold uppercase tracking-widest text-zinc-400">Cliente</p>
            <p className="text-xs font-bold text-[#09090B] truncate">{job.company || job.client_name || "-"}</p>
            {job.company && job.client_name && <p className="text-[10px] text-zinc-500 truncate">{job.client_name}</p>}
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Azioni</p>

          {job.status !== "consegnato" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                {job.status === "in_attesa" && <button onClick={() => updateStatus(job, "ritirato")} disabled={saving} className="h-11 bg-blue-600 text-white text-xs font-bold uppercase rounded-sm flex items-center justify-center gap-1.5" data-testid="status-ritirato"><Truck size={14} /> Ritirato</button>}
                {["in_attesa", "ritirato"].includes(job.status) && <button onClick={() => updateStatus(job, "in_transito")} disabled={saving} className="h-11 bg-orange-500 text-white text-xs font-bold uppercase rounded-sm flex items-center justify-center gap-1.5" data-testid="status-in-transito"><Route size={14} /> In Transito</button>}
                {["ritirato", "in_transito"].includes(job.status) && <button onClick={() => updateStatus(job, "in_consegna")} disabled={saving} className="h-11 bg-amber-500 text-white text-xs font-bold uppercase rounded-sm flex items-center justify-center gap-1.5" data-testid="status-in-consegna"><Navigation size={14} /> In Consegna</button>}
              </div>

              <button onClick={() => navigateTo("pod")} className="w-full h-12 bg-green-600 text-white text-sm font-bold uppercase rounded-sm flex items-center justify-center gap-2" data-testid="complete-delivery-btn">
                <Check size={18} /> Conferma Consegna (POD)
              </button>
            </>
          )}

          {job.status === "consegnato" && (
            <button onClick={() => navigateTo("pod")} className="w-full h-12 bg-[#09090B] text-white text-sm font-bold uppercase rounded-sm flex items-center justify-center gap-2" data-testid="edit-completed-delivery-btn">
              <PenLine size={18} /> Modifica consegna completata
            </button>
          )}

          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => capturePhoto((p) => { toast.info("Foto salvata per il POD"); setPodPhoto(p); })} className="h-11 bg-white border border-zinc-200 text-xs font-bold uppercase rounded-sm flex items-center justify-center gap-1.5 text-zinc-700" data-testid="take-photo-btn">
              <Camera size={14} /> Scatta Foto
            </button>
            <button onClick={() => setShowAnomaly(true)} className="h-11 bg-white border border-red-200 text-xs font-bold uppercase rounded-sm flex items-center justify-center gap-1.5 text-red-600" data-testid="report-anomaly-btn">
              <AlertTriangle size={14} /> Segnala Anomalia
            </button>
          </div>
          <button onClick={toggleCamera} data-testid="toggle-camera-btn"
            className="w-full h-9 bg-zinc-100 border border-zinc-200 text-[10px] font-bold uppercase rounded-sm flex items-center justify-center gap-1.5 text-zinc-500">
            <SwitchCamera size={13} /> {preferredCamera === "environment" ? "Cam: Posteriore" : "Cam: Anteriore"}
          </button>
        </div>
      </div>

      {showAnomaly && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end" onClick={() => setShowAnomaly(false)}>
          <div className="bg-white w-full rounded-t-2xl p-4 space-y-3 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="anomaly-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Segnala Anomalia</h3>
              <button onClick={() => setShowAnomaly(false)} className="text-zinc-400"><X size={20} /></button>
            </div>
            <div className="space-y-2">
              {ANOMALY_TYPES.map((t) => (
                <button key={t} onClick={() => setAnomalyType(t)} className={`w-full h-11 px-4 rounded-sm text-sm font-bold text-left border ${anomalyType === t ? "border-red-500 bg-red-50 text-red-700" : "border-zinc-200 text-zinc-600"}`} data-testid={`anomaly-type-${t}`}>{t}</button>
              ))}
            </div>
            <textarea value={anomalyNote} onChange={(e) => setAnomalyNote(e.target.value)} placeholder="Note aggiuntive..." className="w-full h-20 px-3 py-2 border border-zinc-200 rounded-sm text-sm resize-none" />
            <button onClick={() => capturePhoto(setAnomalyPhoto)} className="w-full h-10 border border-dashed border-zinc-300 rounded-sm text-xs text-zinc-500 flex items-center justify-center gap-1.5">
              <Camera size={14} /> {anomalyPhoto ? "Foto allegata - Cambia" : "Allega foto (opzionale)"}
            </button>
            <button onClick={submitAnomaly} disabled={saving || !anomalyType} className="w-full h-12 bg-red-600 text-white font-bold uppercase rounded-sm text-sm disabled:opacity-50 flex items-center justify-center gap-2" data-testid="submit-anomaly-btn">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <><Send size={16} /> Invia Segnalazione</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
